import React, {useEffect, useMemo, useState} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  TouchableWithoutFeedback,
  Dimensions
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useDispatch} from 'react-redux';
import {
  fetchCartZoneAccesses,
  updateCartZoneAccessOptional
} from '../../actions/zoneActions';
import { SearchScanpackOrder } from '../../actions/scanpackAction';
import CommonStyles from '../../style/commonStyles';
import I18n from 'i18n-js';
import styles, {
  ZONE_ACCESS_ACTIVE_GRADIENT_UP,
  ZONE_ACCESS_ACTIVE_GRADIENT_DOWN,
  ZONE_ACCESS_ACTIVE_BORDER_COLORS,
  ZONE_ACCESS_INACTIVE_GRADIENT_UP,
  ZONE_ACCESS_INACTIVE_GRADIENT_DOWN,
  ZONE_ACCESS_INACTIVE_BORDER_COLORS,
  ZONE_ACCESS_ALLOWED_TEXT_COLOR,
  ZONE_ACCESS_UNALLOWED_TEXT_COLOR
} from '../../style/scanpack';

export const CartOptionalZoneAccessModal = ({
  isVisible,
  onClose,
  cartId,
  cartName,
  scanBarcodePayload
}) => {
  const dispatch = useDispatch();
  const screenWidth = Dimensions.get('window').width;
  const [zoneAccesses, setZoneAccesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const optionalZones = useMemo(() => {
    if (!zoneAccesses || !Array.isArray(zoneAccesses)) return [];
    return zoneAccesses.filter(acc => acc.access_level === 'optional');
  }, [zoneAccesses]);

  useEffect(() => {
    if (!isVisible || !cartId) {
      setZoneAccesses([]);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    dispatch(
      fetchCartZoneAccesses(cartId, (data) => {
        setZoneAccesses(Array.isArray(data) ? data : []);
        setLoading(false);
      })
    );
  }, [isVisible, cartId, dispatch]);

  const handleZoneButtonPress = (access) => {
    const isAllowed = access.optional_zone_access === true;
    const newValue = !isAllowed;
    dispatch(
      updateCartZoneAccessOptional(cartId, access.id, newValue, () => {
        dispatch(fetchCartZoneAccesses(cartId, (data) => {
          setZoneAccesses(Array.isArray(data) ? data : []);
        }));
      })
    );
  };

  const handleSaveAndClose = () => {
    if (scanBarcodePayload) {
      setSaving(true);
      const payloadWithZoneAccess = {
        ...scanBarcodePayload,
        zone_access_specified: true
      };
      dispatch(
        SearchScanpackOrder(payloadWithZoneAccess, () => {
          setSaving(false);
          onClose();
        })
      );
    } else {
      onClose();
    }
  };

  if (!isVisible) return null;

  return (
    <Modal
      visible={isVisible}
      animationType="fade"
      transparent
      onRequestClose={onClose}>
      <TouchableOpacity
        style={styles.zoneAccessModalOverlay}
        activeOpacity={1}
        onPress={onClose}>
        <TouchableWithoutFeedback>
          <View style={styles.zoneAccessModalContent}>
            <LinearGradient
              colors={['#232931', '#232931']}
              start={{x: 0, y: 0}}
              end={{x: 1, y: 0}}
              style={styles.zoneAccessModalContainer}>
              <View style={styles.zoneAccessModalHeader}>
                <Text style={styles.zoneAccessModalTitle} numberOfLines={1}>
                  {cartName
                    ? I18n.t('zoneAccess.cartOptionalZoneTitle', {
                        cartName
                      })
                    : I18n.t('zoneAccess.specifyZoneAccess')}
                </Text>
                <TouchableOpacity
                  testID="closeCartZoneAccessModal"
                  onPress={onClose}
                  style={styles.zoneAccessModalCloseButton}>
                  <Text style={styles.zoneAccessModalCloseButtonText}>×</Text>
                </TouchableOpacity>
              </View>

              {loading ? (
                <View style={styles.zoneAccessModalLoadingContainer}>
                  <ActivityIndicator size="large" color={ZONE_ACCESS_INACTIVE_GRADIENT_DOWN} />
                </View>
              ) : (
                <View style={styles.zoneAccessModalBody}>
                  <Text style={styles.zoneAccessModalSubtitle}>
                    {I18n.t('zoneAccess.optionalZonesLabel')}
                  </Text>
                  {optionalZones.length === 0 ? (
                    <Text style={styles.zoneAccessModalEmptyText}>
                      {I18n.t('zoneAccess.noOptionalZones')}
                    </Text>
                  ) : (
                    <View style={[styles.zoneAccessButtonRow, {maxWidth: screenWidth}]}>
                      {optionalZones.map(access => {
                        const zoneName =
                          access.zone?.name ?? `Zone ${access.zone_id ?? ''}`;
                        const isAllowed =
                          access.optional_zone_access === true;
                        const gradientColors = isAllowed
                          ? [ZONE_ACCESS_ACTIVE_GRADIENT_UP, ZONE_ACCESS_ACTIVE_GRADIENT_DOWN]
                          : [ZONE_ACCESS_INACTIVE_GRADIENT_UP, ZONE_ACCESS_INACTIVE_GRADIENT_DOWN];
                        const borderColors = isAllowed
                          ? ZONE_ACCESS_ACTIVE_BORDER_COLORS
                          : ZONE_ACCESS_INACTIVE_BORDER_COLORS;
                        const textColor = isAllowed
                          ? ZONE_ACCESS_ALLOWED_TEXT_COLOR
                          : ZONE_ACCESS_UNALLOWED_TEXT_COLOR;

                        return (
                          <TouchableOpacity
                            key={
                              access.id ??
                              `${access.zone_id}-${access.zone?.id}`
                            }
                            activeOpacity={0.8}
                            onPress={() => handleZoneButtonPress(access)}
                            testID={`cart-optional-zone-${(zoneName || '')
                              .replace(/\s+/g, '-')
                              .toLowerCase()}`}>
                            <LinearGradient
                              colors={borderColors}
                              style={styles.zoneAccessButtonBorder}
                              start={{x: 0.5, y: 0}}
                              end={{x: 0.5, y: 1}}>
                              <LinearGradient
                                colors={gradientColors}
                                style={styles.zoneAccessButtonInner}
                                start={{x: 0.5, y: 0}}
                                end={{x: 0.5, y: 1}}>
                                <Text
                                  style={[styles.zoneAccessButtonText, {color: textColor}]}>
                                  {zoneName}
                                </Text>
                              </LinearGradient>
                            </LinearGradient>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  )}
                </View>
              )}

              {error ? (
                <Text style={styles.zoneAccessModalErrorText}>{error}</Text>
              ) : null}

              <TouchableOpacity
                testID="saveZoneAccessButton"
                onPress={handleSaveAndClose}
                style={[CommonStyles.saveButton, styles.zoneAccessModalSaveButton]}
                disabled={saving || loading}>
                {saving ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={CommonStyles.saveButtonText}>
                    {I18n.t('zoneAccess.saveAndClose')}
                  </Text>
                )}
              </TouchableOpacity>
            </LinearGradient>
          </View>
        </TouchableWithoutFeedback>
      </TouchableOpacity>
    </Modal>
  );
};
